/**
 * token refresh middleware:
 * - watches for setCredentials in store (or fulfilled fetchToken/refreshToken),
 * - schedules a dispatch of refreshToken some time before expiry.
 *
 * This middleware uses setTimeouts stored in closure and will clear them on logout.
 */
import { clearCredentials, refreshToken, setCredentials } from 'src/configuration/tokenHandling/authSlice';
// const REFRESH_BEFORE_MS = 60_000; // refresh 60s before expiry
const REFRESH_BEFORE_MS = 2000; // refresh 60s before expiry
export const scheduleRefreshMiddleware = (storeAPI) => {
    let timeoutId: null | ReturnType<typeof setTimeout> = null;

    const schedule = (expiresAt: number) => {
        if (!expiresAt) {
            return;
        }
        const now = Date.now();
        const msUntilExpiry = Math.max(expiresAt - now, 0);
        const refreshIn = Math.max(msUntilExpiry - REFRESH_BEFORE_MS, 0);
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(() => {
            // dispatch refreshToken thunk
            storeAPI.dispatch(refreshToken());
        }, refreshIn);
    };

    return (next) => (action) => {
        const result = next(action);
        // handle relevant actions that set/clear tokens
        if (action.type === setCredentials.type) {
            schedule(action.payload.expiresAt);
        } else if (action.type === 'auth/fetchToken/fulfilled' || action.type === 'auth/refreshToken/fulfilled') {
            const state = storeAPI.getState().auth;
            if (state.expiresAt) {
                schedule(state.expiresAt);
            }
        } else if (
            action.type === clearCredentials.type ||
            action.type === 'auth/fetchToken/rejected' ||
            action.type === 'auth/refreshToken/rejected'
        ) {
            if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }
        }
        return result;
    };
};
